import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import FormCard from "../../../components/FormCard";
import { useToast } from "../../../hooks/useToast";
import backArrow from "../../../static/images/backArrow.svg";
import defaultPfp from "../../../static/images/default-pfp.png";
import { appendChatToIndexedDB } from "../../../utils/chatHelper";
import useReliableWebSocket from "../../../utils/useReliableWebSocket";
import classes from "./Profile.module.scss";
// import carImage from "../../../static/images/car-image.svg";
import { APIContext } from "../../../connector";
import { api_url, ws_url } from "../../../constants";
import notificationSound from "../../../static/audio/notification.mp3";
import chatIcon from "../../../static/images/chat-icon.png";

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { openToast } = useToast();
  const [userDetails, setUserDetails] = useState({});
  const { activeTripData, userData } = useContext(APIContext);
  
  const isProfileInActiveTrip = 
    String(activeTripData?.user_details?.user_id) === String(userId) ||
    String(activeTripData?.driver_details?.user_id) === String(userId);
    
  const trip_id = isProfileInActiveTrip && activeTripData?.trip_id ? activeTripData.trip_id : null;

  const tripUrl = trip_id ? `${ws_url}/ws/trip/${trip_id}/` : null;
  //url for images
  const imageUrl = `${api_url}/media/`;

  const { lastJsonMessage: chatMessage } = useReliableWebSocket(tripUrl, {
    share: true,
    shouldReconnect: (closeEvent) => true,
    onError: (error) => {
      console.error("Trip WebSocket error:", error);
    },
    retryOnError: false,
  });

  const fetchUserData = useCallback(async () => {
    try {
      let data = undefined;
      // if passing specific profileData from history via route state
      if (location.state?.profileData && String(location.state.profileData.user_id) === String(userId)) {
        data = location.state.profileData;
      }
      // if viewing own profile (no active trip)
      else if (String(userData?.user_id) === String(userId)) {
        data = userData;
      }
      // if viewing passenger profile in active trip
      else if (String(activeTripData?.user_details?.user_id) === String(userId)) {
        data = activeTripData?.user_details;
      }
      // if viewing driver profile in active trip
      else if (String(activeTripData?.driver_details?.user_id) === String(userId)) {
        data = activeTripData?.driver_details;
      }
      else {
        throw new Error("Profile details not found");
      }

      const userDetails = {
        profile_picture: data?.profile_picture || "",
        email: data?.email || "",
        name: data?.name || "Unknown",
        gender: data?.gender || "Unknown",
        car_plate_number: data?.car_plate_number || data?.driver_info?.car_plate_number || "Unknown",
        car_model: data?.car_model || data?.driver_info?.car_model || "Unknown",
        car_color: data?.car_color || data?.driver_info?.car_color || "Unknown",
        is_driver: data?.user_type === "DRIVER" || Boolean(data?.car_plate_number || data?.driver_info?.car_plate_number),
        is_user: data?.user_type === "CLIENT",
        phone_number: data?.phone_number || "Unknown",
      };

      setUserDetails(userDetails);
    }
    catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [userId, location.state, userData, activeTripData]);

  const playNotificationSound = useCallback(() => {
    const audio = new Audio(notificationSound);
    audio.play().catch(err => {
      console.warn("Sound play blocked by browser until user interacts:", err);
    });
  }, []);

  useEffect(() => {
    if (chatMessage?.type === "chat_message") {
      const newMessage = JSON.parse(chatMessage?.data);
      const text = newMessage.text || "";
      appendChatToIndexedDB(newMessage);
      playNotificationSound();
      openToast(`New message: ${text}`);
    }
  }, [chatMessage, openToast, playNotificationSound])

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  return (
    <div className={classes.container}>
      <FormCard>
        <div className={classes.profileHeader}>
          <div className={classes.backArrow} onClick={() => navigate(-1)}>
            <img
              className={classes.backArrowIcon}
              src={backArrow}
              alt="back arrow"
            />
          </div>
          <div className={classes.profileTitle}>
            <h3>Profile</h3>
          </div>
        </div>
        <div className={classes.profileBody}>
          <div className={classes.profileDetails}>
            {/* Profile picture */}
            <div className={classes.profileImage}>
              {/* Driver's profile picture which includes image of vehichle */}
              {userDetails?.is_driver && (
                <div className={classes.driverProfileImage}>
                  {/* <div className={classes.carImage}>
                    <img src={carImage} alt="car" />
                  </div> */}
                  <div className={classes.userImage}>
                    <img
                      src={`${imageUrl}${userDetails?.profile_picture}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = defaultPfp;
                      }}
                      alt="user" />
                  </div>
                </div>
              )}
              {/* Passenger profile picture which doesn't have vehichle image */}
              {!userDetails?.is_driver && (
                <div className={classes.passengerProfileImage}>
                  <div className={classes.userImage}>
                    <img
                      src={`${imageUrl}${userDetails?.profile_picture}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = defaultPfp;
                      }}
                      alt="user" />
                  </div>
                </div>
              )}
            </div>
            <p className={classes.userName}>
              {userDetails?.name} ({userDetails?.gender})
            </p>

            <div className={classes.email}>
              <p>{userDetails?.email}</p>
            </div>

            {/* Show vehicle details if it's a driver's profile */}
            {userDetails?.is_driver && (
              <div className={classes.carDetails}>
                <div className={classes.carNumber}>
                  <p>{userDetails?.car_plate_number}</p>
                </div>
                <div className={classes.carModel}>
                  <p>
                    {userDetails?.car_color} . {userDetails?.car_model}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </FormCard>

      {/* Show contact buttons if trip is active */}
      {(trip_id !== null) && (
        <FormCard>
          <div className={classes.contactButtons}>
            <div
              className={classes.whatsappButton}
              onClick={() => navigate("/chat")}
            >
              <div className={classes.icon}>
                <img src={chatIcon} />
              </div>
              <p>Chat</p>
            </div>
          </div>
        </FormCard>
      )}
    </div>
  );
};

export default Profile;
